import React, { FC, useState } from 'react'

import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import { CampaignListItem } from 'src/Components/CampaignListItem'
import {
  CampaignPlatformTypeEnum,
  CampaignStatusEnum,
} from 'src/Types/campaigns'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'
import { PrimarySkeleton } from 'src/Components/Skeletons/PrimarySkeleton'
import { useGetNewReleaseQuery } from 'src/Redux/newReleaseApi'
import { CreateCampaignBanner } from 'src/Components/CreateCampaignBanner'
import dayjs from 'src/Helpers/dayjs'
import {
  useCampaignPayments,
  useRetryCampaignPayment,
} from 'src/Hooks/useCampaignPayment'

import { CampaignUnionType } from '../useCampaigns'
import {
  SMS_APPROVAL_QUALIFYING_PLATFORMS,
  SMS_APPROVAL_QUALIFYING_STATUSES,
} from '../useSmsApprovalModal'

import { useRelaunchSpotifyCampaign } from './useRelaunchSpotifyCampaign'
import { CampaignNewReleaseItem } from './CampaignNewReleaseItem/CampaignNewReliseItem'
import { EmptyCampaignState } from './EmptyCampaignState'

const activeCampaignStatuses = [
  CampaignStatusEnum.PENDING,
  CampaignStatusEnum.PENDING_APPROVAL,
  CampaignStatusEnum.AWAITING_REVIEW,
  CampaignStatusEnum.APPROVED,
  CampaignStatusEnum.SCHEDULED,
  CampaignStatusEnum.RUNNING,
  CampaignStatusEnum.INPROCESS,
]

const hasActiveCampaign = (campaigns: CampaignUnionType[]): boolean =>
  campaigns.some((campaign) => activeCampaignStatuses.includes(campaign.status))

interface CampaignListProps {
  loading: boolean
  campaigns: CampaignUnionType[]
  onSmsNotifyClick?: () => void
}

const List = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 40px;
  align-self: stretch;
`
const StyledSkeleton = styled(PrimarySkeleton)`
  height: 120px;
`
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
`

export const CampaignList: FC<CampaignListProps> = (props) => {
  const { loading, campaigns, onSmsNotifyClick } = props
  const navigate = useNavigate()
  const { data: newReleaseInfo } = useGetNewReleaseQuery(null)
  const [relaunch, { isLoading, campaignId }] = useRelaunchSpotifyCampaign()
  const getCampaignPayment = useCampaignPayments()
  const { retryPayment } = useRetryCampaignPayment()
  const [retryingCampaignId, setRetryingCampaignId] = useState<number | null>(
    null,
  )
  if (loading) {
    return (
      <Container data-testid='campaignListSkeleton'>
        <StyledSkeleton />
      </Container>
    )
  }
  if (!campaigns?.length) {
    return <EmptyCampaignState />
  }

  const handleDetailsClick = (
    id: number,
    platform: CampaignPlatformTypeEnum,
  ): void => {
    if (platform === CampaignPlatformTypeEnum.SPOTIFY) {
      navigate(`${ArtistRoutesEnum.SpotifyDetails}/${id}`)
    }
    if (platform === CampaignPlatformTypeEnum.TIKTOK) {
      navigate(`${ArtistRoutesEnum.TiktokDetails}/${id}`)
    }
    if (platform === CampaignPlatformTypeEnum.META) {
      navigate(`${ArtistRoutesEnum.MetaAdsDetails}/${id}`)
    }
  }

  const handleStartCampaignClick = (
    id: number,
    platform: CampaignPlatformTypeEnum,
  ): void => {
    if (platform === CampaignPlatformTypeEnum.SPOTIFY) {
      navigate(`${ArtistRoutesEnum.SpotifySchedule}/${id}`)
    }
    if (platform === CampaignPlatformTypeEnum.TIKTOK) {
      navigate(`${ArtistRoutesEnum.TiktokSchedule}/${id}`)
    }
    if (platform === CampaignPlatformTypeEnum.META) {
      navigate(`${ArtistRoutesEnum.AdsSchedule}/${id}`)
    }
  }

  const handleCreateCampaignClick = (): void => {
    navigate(ArtistRoutesEnum.ChooseCreateCampaignPlatform)
  }

  const handleRetryPaymentClick = async (
    id: number,
    platform: CampaignPlatformTypeEnum,
    orderId: number,
  ): Promise<void> => {
    setRetryingCampaignId(id)
    const isRetried = await retryPayment(platform, id, orderId)
    if (!isRetried) {
      setRetryingCampaignId(null)
    }
  }

  const handleAddTrackLinkClick = (id: number): void => {
    navigate(`${ArtistRoutesEnum.SpotifyDetails}/${id}`)
  }

  const showCreateCampaignBanner =
    !newReleaseInfo && !hasActiveCampaign(campaigns)

  return (
    <List>
      <CampaignNewReleaseItem />
      {showCreateCampaignBanner && (
        <CreateCampaignBanner onCreateClick={handleCreateCampaignClick} />
      )}
      {campaigns.map((item) => {
        const releaseDatePassed = item.releaseDate
          ? dayjs().isAfter(dayjs.utc(item.releaseDate))
          : false
        const hasReceivedPlacements = (item.placementsCount ?? 0) > 0
        const hasTrackLinkSubmitted = Boolean(item.trackSpotifyId)
        const payment = getCampaignPayment(item.platform, item.id)
        const isSmsNotifyEligibleRow =
          SMS_APPROVAL_QUALIFYING_PLATFORMS.includes(item.platform) &&
          SMS_APPROVAL_QUALIFYING_STATUSES.includes(item.status)

        return (
          <CampaignListItem
            key={item.id}
            hasNewRelease={Boolean(newReleaseInfo)}
            matchingType={item.matchingType}
            relaunched={item.relaunched}
            relaunchLoading={isLoading && campaignId === item.id}
            trackImage={item.trackImage}
            trackName={item.trackName}
            artistName={item.artistName}
            platform={
              item.isReels ? CampaignPlatformTypeEnum.REELS : item.platform
            }
            status={item.status}
            statusText={item.statusText}
            paymentState={payment?.state}
            retryPaymentLoading={retryingCampaignId === item.id}
            onRetryPaymentClick={() =>
              payment &&
              handleRetryPaymentClick(item.id, item.platform, payment.orderId)
            }
            isPreRelease={item.isPreRelease}
            releaseDatePassed={releaseDatePassed}
            hasReceivedPlacements={hasReceivedPlacements}
            hasTrackLinkSubmitted={hasTrackLinkSubmitted}
            onDetailsClick={() => handleDetailsClick(item.id, item.platform)}
            onStartCampaignClick={() =>
              handleStartCampaignClick(item.id, item.platform)
            }
            onRelaunchCampaignClick={() => relaunch(item.id)}
            onAddTrackLinkClick={() => handleAddTrackLinkClick(item.id)}
            onSmsNotifyClick={
              isSmsNotifyEligibleRow ? onSmsNotifyClick : void 0
            }
          />
        )
      })}
    </List>
  )
}
